import React from 'react';
import type { Editor } from '@tiptap/react';
import { IconButton, Divider, Toolbar, CircularProgress } from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  Code,
  FormatQuote,
  Image as ImageIcon,
  AttachFile as AttachFileIcon,
} from '@mui/icons-material';

type RichTextToolbarProps = {
  editor: Editor;
  enableMediaUpload: boolean;
  canUploadMedia: boolean;
  isFlushingPending: boolean;
  resolvedUploadContextId?: string | null;
  imageInputRef: React.RefObject<HTMLInputElement | null>;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onImageInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFileInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

export const RichTextToolbar: React.FC<RichTextToolbarProps> = ({
  editor,
  enableMediaUpload,
  canUploadMedia,
  isFlushingPending,
  resolvedUploadContextId,
  imageInputRef,
  fileInputRef,
  onImageInputChange,
  onFileInputChange,
}) => {
  return (
    <Toolbar
      variant="dense"
      sx={{
        minHeight: 42,
        gap: 0.5,
        px: 1,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().toggleBold().run()}
        color={editor.isActive('bold') ? 'primary' : 'default'}
      >
        <FormatBold fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        color={editor.isActive('italic') ? 'primary' : 'default'}
      >
        <FormatItalic fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        color={editor.isActive('strike') ? 'primary' : 'default'}
      >
        <FormatUnderlined fontSize="small" />
      </IconButton>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      <IconButton
        size="small"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        color={editor.isActive('bulletList') ? 'primary' : 'default'}
      >
        <FormatListBulleted fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        color={editor.isActive('orderedList') ? 'primary' : 'default'}
      >
        <FormatListNumbered fontSize="small" />
      </IconButton>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      <IconButton
        size="small"
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        color={editor.isActive('codeBlock') ? 'primary' : 'default'}
      >
        <Code fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        color={editor.isActive('blockquote') ? 'primary' : 'default'}
      >
        <FormatQuote fontSize="small" />
      </IconButton>

      {enableMediaUpload && (
        <>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <IconButton
            size="small"
            onClick={() => imageInputRef.current?.click()}
            disabled={!canUploadMedia || isFlushingPending}
            title={
              !resolvedUploadContextId
                ? 'Save task first to attach media'
                : 'Attach image (uploaded on Save)'
            }
          >
            <ImageIcon fontSize="small" />
          </IconButton>

          <IconButton
            size="small"
            onClick={() => fileInputRef.current?.click()}
            disabled={!canUploadMedia || isFlushingPending}
            title={
              !resolvedUploadContextId
                ? 'Save task first to attach media'
                : 'Attach file (uploaded on Save)'
            }
          >
            <AttachFileIcon fontSize="small" />
          </IconButton>

          {isFlushingPending && <CircularProgress size={16} sx={{ ml: 0.75 }} />}

          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={onImageInputChange}
          />

          <input
            ref={fileInputRef}
            type="file"
            style={{ display: 'none' }}
            onChange={onFileInputChange}
          />
        </>
      )}
    </Toolbar>
  );
};
